import type { AssetMap } from "../assets/loader";
import { aabb } from "./Obstacle";
import { Player } from "./Player";
import { Score } from "./Score";
import { Spawner } from "./Spawner";

export type Biome = "day" | "night" | "sewer" | "hell";

const BIOME_CYCLE = 16000;
const BIOME_TRANSITION = 300;

interface BiomeColors {
  sky: [string, string, string];
  ground: string;
  groundGrass: string;
  dirt: string;
  cloudAlpha: number;
  tint: string;
  pitBottom: string;
}

const BIOMES: Record<Biome, BiomeColors> = {
  day: {
    sky: ["#7ec8e3", "#c5e6a0", "#6a9a45"],
    ground: "#4f8a38",
    groundGrass: "#4f8a38",
    dirt: "#6b4a2a",
    cloudAlpha: 0.92,
    tint: "",
    pitBottom: "#e08920",
  },
  night: {
    sky: ["#0a0a2a", "#1a1040", "#0d0820"],
    ground: "#1a3a1a",
    groundGrass: "#1a3a1a",
    dirt: "#2a1a10",
    cloudAlpha: 0.3,
    tint: "rgba(20,10,60,0.3)",
    pitBottom: "#2020a0",
  },
  sewer: {
    sky: ["#1a2a1a", "#2a3a20", "#1a2010"],
    ground: "#3a3a3a",
    groundGrass: "#2a4a2a",
    dirt: "#2a2a2a",
    cloudAlpha: 0,
    tint: "rgba(20,40,10,0.2)",
    pitBottom: "#30a030",
  },
  hell: {
    sky: ["#4a0000", "#8a1a00", "#2a0000"],
    ground: "#3a2020",
    groundGrass: "#4a1a1a",
    dirt: "#2a1010",
    cloudAlpha: 0.4,
    tint: "rgba(80,10,0,0.15)",
    pitBottom: "#ff3000",
  },
};

const BIOME_ORDER: Biome[] = ["day", "night", "sewer", "hell"];

export class World {
  readonly width = 1280;
  readonly height = 720;
  readonly groundY = 580;
  /** Player lane on screen (world X = screenX + camX) */
  readonly playerScreenX = 200;
  scrollSpeed = 280;
  distanceX = 0;
  camX = 0;
  shake = 0;
  readonly player: Player;
  readonly spawner = new Spawner();
  readonly score = new Score();
  private readonly assets: AssetMap;
  private clouds: { x: number; y: number; s: number }[] = [];
  private reducedMotion = false;
  private invuln = 0;
  biome: Biome = "day";
  private biomeT = 0;

  // Power-up state
  activePowerUp: "bucket_power" | "joint" | "forty_oz" | null = null;
  powerUpTimer = 0;
  canDoubleJump = false;
  private reversedControls = false;
  private speedMultiplier = 1;
  lastPickupType: "normal" | "powerup" | "powerdown" = "normal";

  constructor(assets: AssetMap) {
    this.assets = assets;
    this.player = new Player(assets, this.groundY);
    this.reducedMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    this.resetClouds();
  }

  reset(): void {
    this.scrollSpeed = 280;
    this.distanceX = 0;
    this.camX = 0;
    this.shake = 0;
    this.invuln = 0.6;
    this.biome = "day";
    this.biomeT = 0;
    this.activePowerUp = null;
    this.powerUpTimer = 0;
    this.canDoubleJump = false;
    this.reversedControls = false;
    this.speedMultiplier = 1;
    this.player.reset(this.groundY);
    this.player.x = this.playerScreenX;
    this.spawner.reset();
    this.score.reset();
    this.resetClouds();
  }

  private resetClouds(): void {
    this.clouds = Array.from({ length: 6 }, (_, i) => ({
      x: i * 200 + Math.random() * 40,
      y: 36 + Math.random() * 90,
      s: 0.55 + Math.random() * 0.55,
    }));
  }

  get difficulty(): number {
    return Math.min(1, this.distanceX / 14000);
  }

  update(
    dt: number,
    jumpPressed: boolean,
    jumpHeld: boolean,
    duckHeld: boolean,
    onJump: () => void,
    onCollect: () => void,
    onDeath: () => void,
    onMilestone?: () => void
  ): void {
    // Always decay shake even after death
    if (this.shake > 0) this.shake = Math.max(0, this.shake - dt * 3);

    if (!this.player.alive) return;

    this.scrollSpeed = 280 + this.difficulty * 220;

    // Biome cycling based on distance
    const cyclePos = this.distanceX % BIOME_CYCLE;
    const biomeIdx = Math.floor(cyclePos / 4000);
    this.biome = BIOME_ORDER[biomeIdx % 4];
    const intoSegment = cyclePos - biomeIdx * 4000;
    this.biomeT = intoSegment < BIOME_TRANSITION ? intoSegment / BIOME_TRANSITION : 1;

    // Power-up timer
    if (this.activePowerUp) {
      this.powerUpTimer -= dt;
      if (this.powerUpTimer <= 0) {
        this.clearPowerUp();
      }
    }

    const effectiveSpeed = this.scrollSpeed * this.speedMultiplier;
    this.distanceX += effectiveSpeed * dt;
    this.camX = this.distanceX;
    this.score.addDistance(effectiveSpeed * dt);
    if (onMilestone && this.score.checkMilestone()) onMilestone();
    this.invuln = Math.max(0, this.invuln - dt);

    if (jumpPressed) this.player.requestJump();
    this.player.hasDoubleJump = this.canDoubleJump;

    // Player stays fixed on screen; world X for feet
    this.player.x = this.playerScreenX;
    const feetWorldX = this.camX + this.playerScreenX;
    const ground = this.spawner.groundAt(feetWorldX, this.groundY);
    const effectiveGround = ground ?? this.groundY + 480;

    this.player.update(dt, duckHeld, jumpHeld, effectiveGround, onJump);

    if (ground === null && this.player.y > this.groundY + 50) {
      this.kill(onDeath);
      return;
    }

    this.spawner.update(
      dt,
      this.camX,
      this.groundY,
      this.scrollSpeed,
      this.difficulty
    );

    // Player hitbox in world space for collisions
    const local = this.player.hitbox;
    const pb = {
      x: local.x + this.camX,
      y: local.y,
      w: local.w,
      h: local.h,
    };

    for (const c of this.spawner.collectibles) {
      if (!c.active) continue;
      if (aabb(pb, c.hitbox)) {
        c.active = false;
        if (c.isPowerUp) {
          this.lastPickupType = "powerup";
          this.applyPowerUp(c.kind as "bucket_power" | "joint" | "forty_oz");
          onCollect();
        } else if (c.isPowerDown) {
          this.lastPickupType = "powerdown";
          this.applyPowerDown(c.kind === "hennessy" ? "hennessy" : "crack");
          onCollect();
        } else {
          this.lastPickupType = "normal";
          this.score.addBonus(c.value);
          onCollect();
        }
      }
    }

    if (this.invuln <= 0 && !this.isInvincible) {
      const speedBoost = this.scrollSpeed * (1 / 60);
      for (const o of this.spawner.obstacles) {
        if (!o.active || o.kind === "gap") continue;
        const hb = o.hitbox;
        const swept = { x: hb.x - speedBoost, y: hb.y, w: hb.w + speedBoost, h: hb.h };
        if (!aabb(pb, swept)) continue;

        if (o.duckable && this.player.ducking && this.player.grounded) {
          continue;
        }

        // Jump-clear low ground hazards when feet are above them
        if (o.groundHazard) {
          const feet = pb.y + pb.h;
          if (feet < o.y - 12) continue;
        }

        this.kill(onDeath);
        return;
      }
    } else if (this.isInvincible) {
      // Smash through obstacles when invincible
      const speedBoost = this.scrollSpeed * (1 / 60);
      for (const o of this.spawner.obstacles) {
        if (!o.active || o.kind === "gap") continue;
        const hb = o.hitbox;
        const swept = { x: hb.x - speedBoost, y: hb.y, w: hb.w + speedBoost, h: hb.h };
        if (aabb(pb, swept)) {
          o.active = false;
          this.score.addBonus(5);
        }
      }
    }

    for (const cloud of this.clouds) {
      cloud.x -= this.scrollSpeed * 0.12 * dt * cloud.s;
      if (cloud.x < this.camX - 120) {
        cloud.x = this.camX + this.width + Math.random() * 180;
        cloud.y = 36 + Math.random() * 90;
      }
    }
  }

  private kill(onDeath: () => void): void {
    if (!this.player.alive) return;
    this.player.alive = false;
    this.shake = this.reducedMotion ? 0 : 0.3;
    this.score.commitBest();
    onDeath();
  }

  decayShake(dt: number): void {
    if (this.shake > 0) this.shake = Math.max(0, this.shake - dt * 3);
  }

  applyPowerUp(kind: "bucket_power" | "joint" | "forty_oz"): void {
    this.clearPowerUp();
    this.activePowerUp = kind;
    switch (kind) {
      case "bucket_power":
        this.powerUpTimer = 5;
        break;
      case "joint":
        this.powerUpTimer = 8;
        this.speedMultiplier = 0.5;
        break;
      case "forty_oz":
        this.powerUpTimer = 6;
        this.canDoubleJump = true;
        break;
    }
  }

  applyPowerDown(kind: "hennessy" | "crack"): void {
    this.clearPowerUp();
    this.activePowerUp = null;
    switch (kind) {
      case "hennessy":
        this.reversedControls = true;
        this.powerUpTimer = 6;
        this.activePowerUp = "joint"; // reuse timer
        break;
      case "crack":
        this.speedMultiplier = 1.8;
        this.powerUpTimer = 4;
        this.activePowerUp = "bucket_power"; // reuse timer
        break;
    }
  }

  private clearPowerUp(): void {
    this.activePowerUp = null;
    this.powerUpTimer = 0;
    this.speedMultiplier = 1;
    this.canDoubleJump = false;
    this.reversedControls = false;
  }

  get isInvincible(): boolean {
    return this.activePowerUp === "bucket_power";
  }

  get controlsReversed(): boolean {
    return this.reversedControls;
  }

  private biomeColors(): BiomeColors {
    return BIOMES[this.biome];
  }

  draw(ctx: CanvasRenderingContext2D): void {
    const w = this.width;
    const h = this.height;
    const sx = this.reducedMotion ? 0 : (Math.random() - 0.5) * this.shake * 14;
    const sy = this.reducedMotion ? 0 : (Math.random() - 0.5) * this.shake * 10;
    const bc = this.biomeColors();

    ctx.save();
    ctx.translate(sx, sy);

    // Sky gradient from biome
    const grad = ctx.createLinearGradient(0, 0, 0, h);
    grad.addColorStop(0, bc.sky[0]);
    grad.addColorStop(0.5, bc.sky[1]);
    grad.addColorStop(1, bc.sky[2]);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);

    // Biome-specific background details
    this.drawBiomeBackground(ctx);

    // Skyline parallax
    const sky = this.assets["bg.skyline"];
    const parallax = this.camX * 0.3;
    const tileW = Math.max(1, sky.width * 2.4);
    let start = -((parallax % tileW) + tileW) % tileW;
    if (this.biome === "sewer") {
      ctx.globalAlpha = 0.3;
    } else if (this.biome === "night") {
      ctx.globalAlpha = 0.5;
    }
    for (let x = start; x < w + tileW; x += tileW) {
      ctx.drawImage(sky, x, 100, tileW, 240);
    }
    ctx.globalAlpha = 1;

    // Clouds (hidden in sewer)
    if (bc.cloudAlpha > 0) {
      const cloudImg = this.assets["bg.cloud"];
      for (const c of this.clouds) {
        ctx.globalAlpha = bc.cloudAlpha;
        ctx.drawImage(cloudImg, c.x - this.camX, c.y, 96 * c.s, 48 * c.s);
      }
      ctx.globalAlpha = 1;
    }

    this.drawGround(ctx);

    for (const o of this.spawner.obstacles) {
      o.draw(ctx, this.camX, this.assets);
    }
    for (const c of this.spawner.collectibles) {
      c.draw(ctx, this.camX, this.assets);
    }

    // Funny environmental signs near gaps
    this.drawSigns(ctx);

    this.player.draw(ctx);

    this.drawForegroundDirt(ctx);

    // Biome tint overlay
    if (bc.tint) {
      ctx.fillStyle = bc.tint;
      ctx.fillRect(0, 0, w, h);
    }

    // Joint power-up trippy effect
    if (this.activePowerUp === "joint") {
      const t = performance.now() * 0.001;
      ctx.fillStyle = `hsla(${(t * 60) % 360}, 80%, 50%, 0.06)`;
      ctx.fillRect(0, 0, w, h);
    }

    // Power-up HUD indicator
    this.drawPowerUpHud(ctx);

    ctx.restore();
  }

  private gaps() {
    return this.spawner.obstacles.filter((o) => o.active && o.kind === "gap");
  }

  private inGap(worldX: number): boolean {
    return this.gaps().some(
      (g) => worldX >= g.x && worldX <= g.x + g.gapWidth
    );
  }

  private drawForegroundDirt(ctx: CanvasRenderingContext2D): void {
    const w = this.width;
    const h = this.height;
    const gy = this.groundY;
    const step = 4;
    const bc = this.biomeColors();

    ctx.fillStyle = bc.dirt;
    for (let sx = 0; sx < w; sx += step) {
      const wx = this.camX + sx + step / 2;
      if (this.inGap(wx)) continue;
      ctx.fillRect(sx, gy + 4, step + 1, h - gy);
    }

    ctx.fillStyle = bc.groundGrass;
    for (let sx = 0; sx < w; sx += step) {
      const wx = this.camX + sx + step / 2;
      if (this.inGap(wx)) continue;
      ctx.fillRect(sx, gy, step + 1, 5);
    }
  }

  private drawGround(ctx: CanvasRenderingContext2D): void {
    const tile = this.assets["tiles.ground"];
    const tileW = 64;
    const gaps = this.gaps();
    const h = this.height;
    const gy = this.groundY;

    // Deep Mario pits first (so ground tiles layer correctly)
    for (const g of gaps) {
      this.drawPit(ctx, g.x, g.gapWidth, gy, h);
    }

    const start = Math.floor(this.camX / tileW) * tileW - tileW;
    for (let x = start; x < this.camX + this.width + tileW * 2; x += tileW) {
      const mid = x + tileW / 2;
      if (gaps.some((g) => mid >= g.x && mid <= g.x + g.gapWidth)) continue;
      ctx.drawImage(tile, x - this.camX, gy - 10, tileW, 72);
    }
  }

  private readonly SIGN_TEXTS = [
    "FREE WIFI →",
    "SAFE ZONE",
    "NO COPS",
    "EAT HERE",
    "JOB CENTER →",
    "DANGER",
    "RIP",
    "EPSTEIN WUZ HERE",
    "SEND NUDES",
    "LIGMA",
  ];

  private drawSigns(ctx: CanvasRenderingContext2D): void {
    const gy = this.groundY;
    const signInterval = 2200;
    const startX = Math.floor(this.camX / signInterval) * signInterval;

    ctx.save();
    ctx.font = "bold 9px sans-serif";
    for (let wx = startX; wx < this.camX + this.width + signInterval; wx += signInterval) {
      if (this.inGap(wx)) continue;
      const hash = (wx * 7919) & 0xffff;
      if ((hash % 3) !== 0) continue;
      const sx = wx - this.camX;
      const text = this.SIGN_TEXTS[hash % this.SIGN_TEXTS.length];

      // Sign post
      ctx.fillStyle = "#5a3a1a";
      ctx.fillRect(sx + 8, gy - 44, 4, 44);
      // Sign board
      ctx.fillStyle = "#e8d8b0";
      ctx.fillRect(sx - 10, gy - 50, 50, 18);
      ctx.strokeStyle = "#3a2a10";
      ctx.lineWidth = 1;
      ctx.strokeRect(sx - 10, gy - 50, 50, 18);
      // Text
      ctx.fillStyle = "#2a1a0a";
      ctx.fillText(text, sx - 6, gy - 37);
    }
    ctx.restore();
  }

  private drawBiomeBackground(ctx: CanvasRenderingContext2D): void {
    const w = this.width;
    const t = performance.now() * 0.001;

    switch (this.biome) {
      case "night": {
        // Stars
        ctx.fillStyle = "#ffffff";
        for (let i = 0; i < 30; i++) {
          const sx = ((i * 137 + 42) % w);
          const sy = (i * 73 + 11) % 200;
          const twinkle = Math.sin(t * 2 + i) * 0.5 + 0.5;
          ctx.globalAlpha = twinkle * 0.8;
          ctx.fillRect(sx, sy, 2, 2);
        }
        ctx.globalAlpha = 1;
        // Flashing police lights in background
        const flashOn = Math.sin(t * 4) > 0;
        if (flashOn) {
          ctx.fillStyle = "rgba(0,80,255,0.15)";
          ctx.fillRect(w * 0.7, 140, 60, 30);
          ctx.fillStyle = "rgba(255,0,0,0.15)";
          ctx.fillRect(w * 0.75, 140, 60, 30);
        }
        break;
      }
      case "sewer": {
        // Ceiling pipes/drips
        ctx.fillStyle = "#3a3a3a";
        ctx.fillRect(0, 0, w, 40);
        ctx.fillStyle = "#2a2a2a";
        for (let i = 0; i < w; i += 80) {
          ctx.fillRect(i, 0, 12, 60 + Math.sin(i * 0.1) * 20);
        }
        // Dripping water
        ctx.fillStyle = "rgba(80,200,80,0.6)";
        for (let i = 0; i < 8; i++) {
          const dx = ((i * 163 + 20) % w);
          const dy = 40 + (((t * 60 + i * 50) % 80));
          ctx.fillRect(dx, dy, 2, 6);
        }
        break;
      }
      case "hell": {
        // Background flames
        ctx.globalAlpha = 0.4;
        for (let i = 0; i < 12; i++) {
          const fx = (i * 110 + 30) % w;
          const fh = 40 + Math.sin(t * 3 + i) * 20;
          const flameGrad = ctx.createLinearGradient(fx, 340 - fh, fx, 340);
          flameGrad.addColorStop(0, "rgba(255,200,0,0)");
          flameGrad.addColorStop(0.5, "rgba(255,100,0,0.6)");
          flameGrad.addColorStop(1, "rgba(200,0,0,0.8)");
          ctx.fillStyle = flameGrad;
          ctx.fillRect(fx - 10, 340 - fh, 20, fh);
        }
        ctx.globalAlpha = 1;
        // Rising soul silhouettes
        ctx.fillStyle = "rgba(80,0,0,0.3)";
        for (let i = 0; i < 5; i++) {
          const sx = (i * 260 + 80) % w;
          const sy = 200 + Math.sin(t * 0.5 + i * 2) * 40;
          ctx.beginPath();
          ctx.ellipse(sx, sy, 8, 14, 0, 0, Math.PI * 2);
          ctx.fill();
        }
        break;
      }
    }
  }

  private drawPowerUpHud(ctx: CanvasRenderingContext2D): void {
    if (!this.activePowerUp || this.powerUpTimer <= 0) return;

    const x = 20;
    const y = 60;
    const barW = 80;
    const barH = 8;
    let maxTime = 5;
    let color = "#ffaa00";
    let label = "";

    switch (this.activePowerUp) {
      case "bucket_power":
        maxTime = this.reversedControls ? 6 : (this.speedMultiplier > 1 ? 4 : 5);
        color = this.speedMultiplier > 1 ? "#ff2222" : "#ffaa00";
        label = this.speedMultiplier > 1 ? "CRACK" : (this.reversedControls ? "DRUNK" : "INVINCIBLE");
        break;
      case "joint":
        maxTime = this.reversedControls ? 6 : 8;
        color = this.reversedControls ? "#aa44ff" : "#44ff44";
        label = this.reversedControls ? "HENNESSY" : "SLOWED";
        break;
      case "forty_oz":
        maxTime = 6;
        color = "#4488ff";
        label = "DOUBLE JUMP";
        break;
    }

    const pct = Math.max(0, this.powerUpTimer / maxTime);
    ctx.fillStyle = "rgba(0,0,0,0.5)";
    ctx.fillRect(x - 2, y - 14, barW + 4, barH + 18);
    ctx.fillStyle = color;
    ctx.fillRect(x, y, barW * pct, barH);
    ctx.strokeStyle = "#fff";
    ctx.lineWidth = 1;
    ctx.strokeRect(x, y, barW, barH);
    ctx.fillStyle = "#fff";
    ctx.font = "bold 10px sans-serif";
    ctx.fillText(label, x, y - 3);
  }

  /** Full-depth pit like Super Mario — walls to the bottom of the screen. */
  private drawPit(
    ctx: CanvasRenderingContext2D,
    worldX: number,
    gapW: number,
    gy: number,
    screenH: number
  ): void {
    const x = worldX - this.camX;
    const pitBottom = screenH + 8;

    // Void
    const voidGrad = ctx.createLinearGradient(0, gy, 0, pitBottom);
    voidGrad.addColorStop(0, "#1a100c");
    voidGrad.addColorStop(0.45, "#0d0908");
    voidGrad.addColorStop(1, "#050304");
    ctx.fillStyle = voidGrad;
    ctx.fillRect(x, gy - 2, gapW, pitBottom - gy + 2);

    // Side dirt walls (perspective strips)
    const wallW = 14;
    const wallGradL = ctx.createLinearGradient(x, 0, x + wallW, 0);
    wallGradL.addColorStop(0, "#5a3a22");
    wallGradL.addColorStop(1, "#2a1810");
    ctx.fillStyle = wallGradL;
    ctx.fillRect(x, gy - 2, wallW, pitBottom - gy + 2);

    const wallGradR = ctx.createLinearGradient(x + gapW - wallW, 0, x + gapW, 0);
    wallGradR.addColorStop(0, "#2a1810");
    wallGradR.addColorStop(1, "#5a3a22");
    ctx.fillStyle = wallGradR;
    ctx.fillRect(x + gapW - wallW, gy - 2, wallW, pitBottom - gy + 2);

    // Hot oil / lava at the bottom
    const oilY = pitBottom - 48;
    const oilGrad = ctx.createLinearGradient(0, oilY, 0, pitBottom);
    oilGrad.addColorStop(0, "#e08920");
    oilGrad.addColorStop(0.5, "#c05010");
    oilGrad.addColorStop(1, "#6a2008");
    ctx.fillStyle = oilGrad;
    ctx.fillRect(x + 4, oilY, gapW - 8, pitBottom - oilY);

    // Bubbles
    ctx.fillStyle = "rgba(255,220,100,0.55)";
    const t = performance.now() * 0.004;
    for (let i = 0; i < 5; i++) {
      const bx = x + 18 + i * ((gapW - 36) / 4);
      const by = oilY + 10 + Math.sin(t + i * 1.7) * 6;
      ctx.beginPath();
      ctx.arc(bx, by, 3 + (i % 3), 0, Math.PI * 2);
      ctx.fill();
    }

    // Cliff lips
    ctx.fillStyle = "#3d2818";
    ctx.fillRect(x - 6, gy - 16, 12, 22);
    ctx.fillRect(x + gapW - 6, gy - 16, 12, 22);
    ctx.fillStyle = "#4f8a38";
    ctx.fillRect(x - 6, gy - 16, 12, 5);
    ctx.fillRect(x + gapW - 6, gy - 16, 12, 5);
  }
}
